import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProfileAddressForm from './ProfileAddressForm';
import {
  useCreateUserAddressMutation,
  useUpdateUserAddressMutation,
  useUpdateUserPrimaryAddressMutation,
} from './userAddress.generated';
import { Button, PortalModal } from '@web/utils/';
import { useCustomToast } from '@/hooks/useCustomToast';
import { useUserContext } from '@/provider';
import { Address, AddressInput } from '@/types';

const ProfileAddresses = () => {
  const { user } = useUserContext();
  const { t } = useTranslation();
  const toast = useCustomToast();
  const [isOpen, setIsOpen] = useState(false);
  const [editAddressId, setEditAddressId] = useState<string | null>(null);
  const [primeAddress, setPrimeAddress] = useState<string | null>(null);
  const [coordinates, setCoordinates] = useState({ lat: 0, long: 0 });
  const [form, setForm] = useState({
    addressLine1: '',
    addressLine2: '',
    addressPostcode: '',
    addressLocality: '',
  });

  useEffect(() => {
    setPrimeAddress(user?.primaryAddress.id);
  }, [user]);

  const [_create, createUserAddress] = useCreateUserAddressMutation();
  const [_update, updateUserAddress] = useUpdateUserAddressMutation();
  const [_set, setPrimaryAddress] = useUpdateUserPrimaryAddressMutation();

  const inputHandler = (name: string, value: string | number) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const formData: AddressInput = {
      addressLine1: form.addressLine1,
      addressLine2: form.addressLine2,
      addressPostcode: form.addressPostcode,
      addressLocality: form.addressLocality,
      coordinates: {
        lat: coordinates.lat,
        long: coordinates.long,
      },
    };
    if (editAddressId) {
      try {
        const { data } = await updateUserAddress({
          id: editAddressId,
          address: formData,
        });
        if (data?.updateUserAddress) {
          toast('success', t('profile.address_updated_successfully'));
          setIsOpen(false);
          setEditAddressId(null);
          window.location.reload();
        } else {
          toast('error', t('profile.error_updating_address'));
        }
      } catch (error) {
        toast('error', t('profile.error_updating_address'));
      }
    } else {
      try {
        const { data } = await createUserAddress({
          address: formData,
        });
        if (data?.createUserAddress) {
          toast('success', t('profile.address_created_successfully'));
          setIsOpen(false);
          window.location.reload();
        }
      } catch (error) {
        toast('error', t('profile.error_creating_address'));
      }
    }
  };

  const handleEdit = (address: Address) => {
    setEditAddressId(address.id);
    setIsOpen(true);
    setForm({
      addressLine1: address.addressLine1 || '',
      addressLine2: address.addressLine2 || '',
      addressPostcode: address.addressPostcode || '',
      addressLocality: address.addressLocality || '',
    });
  };

  const handleSetPrimary = async (addressId: string) => {
    try {
      const { data } = await setPrimaryAddress({ addressId });
      if (data?.updateUserPrimaryAddress) {
        toast('success', t('profile.primary_address_set_successfully'));
        setPrimeAddress(addressId);
      }
    } catch (error) {
      toast('error', t('profile.error_setting_primary_address'));
    }
  };

  return (
    <>
      <PortalModal isOpen={isOpen} setIsOpen={setIsOpen}>
        <ProfileAddressForm
          form={form}
          inputHandler={inputHandler}
          setCoordinates={setCoordinates}
          submitHandler={handleSubmit}
          isEdit={editAddressId}
        />
      </PortalModal>
      <div className="py-1-0 flex justify-between">
        <p className="text-2-0 font-bd">{t('profile.my_addresses')}</p>
        <Button onClick={() => setIsOpen(true)} variant="secondary">
          {t('profile.add_new')}
        </Button>
      </div>
      {user?.addresses.length > 0 && (
        <div className="grid grid-cols-2 gap-1-0">
          {user.addresses.map((address: Address) => (
            <div
              key={address.id}
              className="single-address p-1-0 text-body2 rounded-4-0 border-secondary-grey border"
            >
              <div className="pb-1-0">
                <p className="font-bd">{t('profile.address_line1')}</p>
                <p>{address.addressLine1}</p>
                <p>{address.addressLine2}</p>
              </div>
              <div>
                <p className="font-bd">{t('profile.address_locality')}:</p>
                <p>
                  {address.addressLocality}, {address.addressPostcode}
                </p>
              </div>
              <div className="flex w-full justify-between mt-2-0 relative">
                <Button
                  className="scale-75"
                  variant="secondary"
                  onClick={() => handleEdit(address)}
                >
                  {t('profile.edit')}
                </Button>
                {address.id !== primeAddress && (
                  <Button
                    className="scale-75"
                    onClick={() => handleSetPrimary(address.id)}
                  >
                    {t('profile.set_as_primary')}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default ProfileAddresses;
